import {action, computed, makeAutoObservable, observable, reaction, runInAction} from 'mobx';
import {ActiveSubscriptionStatus, ActiveSubscriptionStatusDto} from "../model/billing/ActiveSubscriptionStatusDto";
import {billingApi} from "../backend/BillingApi";
import {configureUser, logEvent} from "../utils/SentryUtils";
import {CompaniesStoreStates, userInfoStore} from "./UserInfo";
import {localStore} from "./localStore";
import {makePersistable, stopPersisting} from "mobx-persist-store";
import {uiStateStore} from "./UiState";

const SUBSCRIPTION_DISABLED = {
    status: ActiveSubscriptionStatus.DISABLED
};

class CompanyStore {
    @observable selectedCompanyId: string = '';

    @observable private subscriptionInfo: ActiveSubscriptionStatusDto = SUBSCRIPTION_DISABLED;

    constructor() {
        makeAutoObservable(this);
        makePersistable(this, {
            name: 'selectedCompanyStore',
            properties: ['selectedCompanyId'],
            storage: localStore
        }).then(() => console.log("CompanyStore hydrated"));
    }

    public stopPersisting = () => {
        stopPersisting(this)
    }

    @action
    public resetSelection() {
        this.selectedCompanyId = '';
        this.subscriptionInfo = SUBSCRIPTION_DISABLED;
    }

    @computed
    get companyId() {
        return this.selectedCompanyId;
    }

    set companyId(companyId: string) {
        runInAction(() => {
            this.selectedCompanyId = companyId;
            this.subscriptionInfo = SUBSCRIPTION_DISABLED;
        })
    }

    public refreshSubscriptionStatus(companyId: string = this.selectedCompanyId) {
        if (companyId) {
            billingApi.fetchSubscriptionStatus(companyId)
                .then(status => {
                    if (this.selectedCompanyId === companyId) {
                        runInAction(() => {
                            logEvent("selectedCompany", "Subscription status fetched")
                            this.subscriptionInfo = status;
                        })
                    } else {
                        logEvent(
                            "selectedCompany",
                            `Subscription status fetched for another company. Captured id: ${companyId}, selected at the moment ${this.selectedCompanyId}`,
                            "error"
                        )
                    }
                }).catch((e) => {
                //Sometimes refresh happens before token is present or one browser is used for different users that has access to different companies
                if (e === 'No current user' || e.message === 'Access to the company denied') {
                    this.subscriptionInfo = SUBSCRIPTION_DISABLED
                } else {
                    throw e;
                }
            })
        } else {
            this.subscriptionInfo = SUBSCRIPTION_DISABLED
        }
    }

    @computed
    public get hasSubscription(): boolean {
        return this.subscriptionInfo.status !== ActiveSubscriptionStatus.DISABLED;
    }

    @computed
    public get subscriptionStatus(): ActiveSubscriptionStatus | undefined {
        return this.subscriptionInfo.status;
    }

    @computed
    public get isLoadingSubscription(): boolean {
        return false;
    }

    @computed
    public get isSelected(): boolean {
        return this.selectedCompanyId !== '';
    }
}

export const selectedCompanyStore = new CompanyStore();


reaction(
    () => selectedCompanyStore.companyId,
    (companyId) => {
        selectedCompanyStore.refreshSubscriptionStatus(companyId)
    }
)

reaction(
    () => userInfoStore.companiesLoadingState,
    () => {
        if (userInfoStore.companiesLoadingState === CompaniesStoreStates.DONE) {
            const companies = userInfoStore.companies;

            if (companies.length === 1) {
                selectedCompanyStore.companyId = companies[0].id;
            } else {
                const userHasThisCompany = companies.find((it) => it.id === selectedCompanyStore.companyId);
                if (!userHasThisCompany) {
                    selectedCompanyStore.resetSelection()
                }
            }
        }
    }
)

reaction(
    () => [userInfoStore.email, selectedCompanyStore.companyId, userInfoStore.findById(selectedCompanyStore.companyId)?.name ?? "", uiStateStore.language],
    ([email, companyId, companyName, language]) => configureUser(email, companyId, companyName, language)
)
